import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, Calendar, Check, Phone, Car } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const LOGO_URL = "/logo.png";

const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Trish Drives Honda" className="h-14 md:h-16 object-contain" />
          </Link>
          
          <nav className="hidden md:flex items-center gap-2">
            <Link to="/" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Home</Link>
            <Link to="/cars" className="text-white bg-[#E91E8C] px-4 py-2 rounded-lg font-medium">Cars</Link>
            <Link to="/#booking-form" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Book Visit</Link>
          </nav>
          
          <Link to="/#booking-form" className="btn-primary text-sm py-3 px-6" data-testid="header-book-btn">
            Book Now
          </Link>
        </div>
      </div>
    </header>
  );
};

// Multi-step Booking Modal
const BookingModal = ({ isOpen, onClose, selectedVehicle, vehicles }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    vehicle_interested: selectedVehicle?.name || "",
    lead_source: "",
    lead_source_other: "",
    notes: "",
    booking_type: "",
    preferred_date: "",
    preferred_time: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedVehicle) {
      setFormData(prev => ({ ...prev, vehicle_interested: selectedVehicle.name }));
    }
  }, [selectedVehicle]);

  const handleClose = () => {
    setStep(1);
    setSubmitted(false);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      vehicle_interested: "",
      lead_source: "",
      lead_source_other: "",
      notes: "",
      booking_type: "",
      preferred_date: "",
      preferred_time: ""
    });
    onClose();
  };

  const handleNext = () => {
    if (!formData.full_name || !formData.email || !formData.phone || !formData.vehicle_interested || !formData.lead_source) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(2);
  };

  const handleBookingTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, booking_type: type }));
    if (type === "consultation_call") {
      handleSubmit(type);
    } else {
      setStep(3);
    }
  };

  const handleSubmit = async (bookingType = formData.booking_type) => {
    if ((bookingType === "showroom_visit" || bookingType === "test_drive") && 
        (!formData.preferred_date || !formData.preferred_time)) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        booking_type: bookingType
      });
      setSubmitted(true);
      toast.success("Thank you! We'll contact you shortly.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const day = date.getDay();
      if (day !== 0 && day !== 6) {
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    return dates;
  };

  const getTimeSlots = () => {
    return [
      "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
      "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
      "3:00 PM", "3:30 PM", "4:00 PM"
    ];
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const leadSources = ["Google Search", "Facebook", "Instagram", "TikTok", "WhatsApp", "Referral", "Returning Customer", "Other"];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#2C2420]">
            {submitted ? "Thank You!" : 
             step === 1 ? "Book Your Visit" : 
             step === 2 ? "Select Booking Type" : 
             "Select Date & Time"}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-[#388E3C]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#388E3C]" />
            </div>
            <p className="text-lg text-[#6B5E55] mb-2">Your booking request has been submitted!</p>
            <p className="text-sm text-[#6B5E55] mb-8">
              We'll contact you within 24 hours to confirm your {formData.booking_type?.replace(/_/g, ' ')}.
            </p>
            <Button onClick={handleClose} className="btn-secondary" data-testid="close-modal-btn">Close</Button>
          </div>
        ) : step === 1 ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-[#FFF5F9] px-4 py-3 rounded-lg border border-[#E91E8C]/20 text-sm text-[#4A4A4A]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#E91E8C] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Showroom visits & test drives at <strong>ATL Hagley Park, Kingston</strong></span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Full Name *</Label>
                <Input type="text" placeholder="John Smith" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="form-input" data-testid="modal-input-name" />
              </div>
              <div className="form-group">
                <Label className="form-label">Email *</Label>
                <Input type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="form-input" data-testid="modal-input-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Phone Number *</Label>
                <Input type="tel" placeholder="+1 876 XXX XXXX" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="form-input" data-testid="modal-input-phone" />
              </div>
              <div className="form-group">
                <Label className="form-label">Vehicle Interested In *</Label>
                <Select value={formData.vehicle_interested} onValueChange={(v) => setFormData({ ...formData, vehicle_interested: v })}>
                  <SelectTrigger className="form-input" data-testid="modal-select-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (<SelectItem key={v.vehicle_id} value={v.name}>{v.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="form-group">
              <Label className="form-label">How Did You Find Us? *</Label>
              <Select value={formData.lead_source} onValueChange={(v) => setFormData({ ...formData, lead_source: v })}>
                <SelectTrigger className="form-input" data-testid="modal-select-source"><SelectValue placeholder="Select an option" /></SelectTrigger>
                <SelectContent>
                  {leadSources.map((source) => (<SelectItem key={source} value={source}>{source}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {formData.lead_source === "Other" && (
              <div className="form-group">
                <Label className="form-label">Please specify</Label>
                <Input type="text" placeholder="How did you hear about us?" value={formData.lead_source_other} onChange={(e) => setFormData({ ...formData, lead_source_other: e.target.value })} className="form-input" />
              </div>
            )}

            <div className="form-group">
              <Label className="form-label">Additional Notes</Label>
              <Textarea placeholder="Any specific questions or requirements?" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="form-input min-h-[80px]" data-testid="modal-input-notes" />
            </div>

            <Button onClick={handleNext} className="w-full btn-primary" data-testid="modal-next-btn">Next</Button>
          </div>
        ) : step === 2 ? (
          <div className="space-y-4 py-4">
            <p className="text-center text-[#4A4A4A] mb-6">How would you like to proceed?</p>
            
            <button onClick={() => handleBookingTypeSelect("consultation_call")} className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group" data-testid="modal-btn-consultation">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Phone className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Consultation Call</h3>
                  <p className="text-sm text-[#6B5E55]">We'll call you to discuss your options</p>
                </div>
              </div>
            </button>

            <button onClick={() => handleBookingTypeSelect("showroom_visit")} className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group" data-testid="modal-btn-showroom">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Car className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Showroom Visit</h3>
                  <p className="text-sm text-[#6B5E55]">Visit us at ATL Hagley Park, Kingston</p>
                </div>
              </div>
            </button>

            <button onClick={() => handleBookingTypeSelect("test_drive")} className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group" data-testid="modal-btn-testdrive">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Calendar className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Test Drive</h3>
                  <p className="text-sm text-[#6B5E55]">Experience the vehicle firsthand</p>
                </div>
              </div>
            </button>

            <button onClick={() => setStep(1)} className="w-full text-center text-[#6B5E55] hover:text-[#E91E8C] transition-colors text-sm mt-4">← Back to details</button>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-2 bg-[#FFF5F9] px-4 py-2 rounded-full text-[#E91E8C] font-medium">
                {formData.booking_type === "showroom_visit" ? (<><Car className="w-4 h-4" /> Showroom Visit</>) : (<><Calendar className="w-4 h-4" /> Test Drive</>)}
              </span>
            </div>

            <div className="form-group">
              <Label className="form-label">Select Date *</Label>
              <Select value={formData.preferred_date} onValueChange={(v) => setFormData({ ...formData, preferred_date: v })}>
                <SelectTrigger className="form-input" data-testid="modal-select-date"><SelectValue placeholder="Choose a weekday" /></SelectTrigger>
                <SelectContent>
                  {getAvailableDates().map((date) => (<SelectItem key={date} value={date}>{formatDateDisplay(date)}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B5E55] mt-1">Available Monday - Friday only</p>
            </div>

            <div className="form-group">
              <Label className="form-label">Select Time *</Label>
              <Select value={formData.preferred_time} onValueChange={(v) => setFormData({ ...formData, preferred_time: v })}>
                <SelectTrigger className="form-input" data-testid="modal-select-time"><SelectValue placeholder="Choose a time slot" /></SelectTrigger>
                <SelectContent>
                  {getTimeSlots().map((time) => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B5E55] mt-1">Available 9:00 AM - 4:00 PM</p>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1">Back</Button>
              <Button onClick={() => handleSubmit()} className="flex-1 btn-primary" disabled={loading} data-testid="modal-submit-btn">
                {loading ? "Submitting..." : "Confirm Booking"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const VehicleCard = ({ vehicle, onBook }) => {
  const formatPrice = (price, currency = "JMD", status = "available") => {
    if (status === "special_order") return "Special Order";
    if (!price || price === 0) return "Contact for Price";
    if (currency === "JMD") {
      if (price >= 1000000) {
        const millions = price / 1000000;
        return `JMD $${millions.toFixed(3)}m`;
      }
      return `JMD $${price.toLocaleString()}`;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = () => {
    if (vehicle.status === "coming_soon") return <div className="absolute top-4 right-4 bg-[#E91E8C] text-white text-xs font-medium px-3 py-1 rounded-full">Coming Soon</div>;
    if (vehicle.status === "sold") return <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-medium px-3 py-1 rounded-full">Sold</div>;
    if (vehicle.status === "special_order") return <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-medium px-3 py-1 rounded-full">Special Order</div>;
    return null;
  };

  return (
    <div className="card-vehicle" data-testid={`vehicle-card-${vehicle.vehicle_id}`}>
      <Link to={`/cars/${vehicle.vehicle_id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#FFF5F9] to-[#FCE4EC]">
          <img
            src={vehicle.images[0]}
            alt={vehicle.name}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = 'flex';
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div
            className="absolute inset-0 flex-col items-center justify-center text-[#E91E8C]/40"
            style={{ display: 'none' }}
            data-testid={`vehicle-image-fallback-${vehicle.vehicle_id}`}
          >
            <Car className="w-16 h-16" />
            <span className="mt-2 text-xs font-medium text-[#6B5E55]">Image unavailable</span>
          </div>
          {getStatusBadge()}
        </div>
      </Link>
      
      <div className="p-6">
        <Link to={`/cars/${vehicle.vehicle_id}`} className="block hover:text-[#E91E8C] transition-colors">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-heading text-xl font-semibold text-[#1A1A1A]">{vehicle.name}</h3>
            <span className="text-xs bg-[#FFF5F9] text-[#E91E8C] px-2 py-1 rounded-full">{vehicle.body_type}</span>
          </div>
        </Link>
        
        <p className="text-2xl font-bold text-[#E91E8C] mb-4">{formatPrice(vehicle.price, vehicle.currency, vehicle.status)}</p>
        
        {vehicle.features && vehicle.features.length > 0 && (
          <ul className="space-y-2 mb-4">
            {vehicle.features.slice(0, 3).map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                <Check className="w-4 h-4 text-[#E91E8C]" />
                {feature}
              </li>
            ))}
            {vehicle.features.length > 3 && (
              <li className="text-sm text-[#E91E8C] font-medium">+{vehicle.features.length - 3} more features</li>
            )}
          </ul>
        )}
        
        <div className="flex gap-2">
          <Link to={`/cars/${vehicle.vehicle_id}`} className="flex-1 text-center py-2.5 border-2 border-[#E91E8C] text-[#E91E8C] rounded-full font-medium hover:bg-[#FFF5F9] transition-colors" data-testid={`view-btn-${vehicle.vehicle_id}`}>
            View Details
          </Link>
          <Button onClick={() => onBook(vehicle)} className="flex-1 btn-primary" disabled={vehicle.status === "sold"} data-testid={`book-btn-${vehicle.vehicle_id}`}>
            Book
          </Button>
        </div>
      </div>
    </div>
  );
};

const CarsPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles`, { params: { show_on_cars: true } });
        setVehicles(response.data);
        setAllVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        toast.error("Failed to load vehicles");
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    let filtered = [...allVehicles];
    if (search) filtered = filtered.filter(v => v.name.toLowerCase().includes(search.toLowerCase()) || v.model.toLowerCase().includes(search.toLowerCase()));
    if (bodyType && bodyType !== "all") filtered = filtered.filter(v => v.body_type === bodyType);
    if (sortBy === "price_asc") filtered.sort((a, b) => a.price - b.price);
    else if (sortBy === "price_desc") filtered.sort((a, b) => b.price - a.price);
    setVehicles(filtered);
  }, [search, sortBy, bodyType, allVehicles]);

  const bodyTypes = [...new Set(allVehicles.map(v => v.body_type))];
  const handleBook = (vehicle) => { setSelectedVehicle(vehicle); setIsModalOpen(true); };
  const clearFilters = () => { setSearch(""); setSortBy(""); setBodyType(""); };

  return (
    <div className="min-h-screen bg-white" data-testid="cars-page">
      <Header />
      
      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">Our Vehicles</h1>
            <p className="text-lg text-[#4A4A4A]">Browse our collection of premium Honda vehicles</p>
          </div>
          
          {/* Filters */}
          <div className="bg-[#FFF5F9] rounded-2xl p-6 shadow-sm border border-[#FFB6D9]/30 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A4A]" />
                <Input type="text" placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-12 form-input bg-white" data-testid="search-input" />
              </div>
              
              <Select value={bodyType} onValueChange={setBodyType}>
                <SelectTrigger className="w-full md:w-48 form-input bg-white" data-testid="filter-body-type"><SelectValue placeholder="Body Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {bodyTypes.map(type => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48 form-input bg-white" data-testid="filter-sort"><SelectValue placeholder="Sort By" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
              
              {(search || sortBy || bodyType) && (
                <Button variant="ghost" onClick={clearFilters} className="text-[#E91E8C]" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />Clear
                </Button>
              )}
            </div>
          </div>
          
          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-12 h-12 spinner"></div></div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20">
              <SlidersHorizontal className="w-12 h-12 text-[#4A4A4A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">No vehicles found</h3>
              <p className="text-[#4A4A4A]">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <p className="text-[#4A4A4A] mb-6">{vehicles.length} vehicles found</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {vehicles.map(vehicle => (<VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} onBook={handleBook} />))}
              </div>
            </>
          )}
        </div>
      </main>
      
      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} selectedVehicle={selectedVehicle} vehicles={allVehicles} />
      
      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img src={LOGO_URL} alt="Trish Drives Honda" className="h-12 mx-auto mb-4" />
          <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Trish Drives Honda. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CarsPage;
